import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface BrandAsset {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  variable_name: string;
  asset_type: 'logo' | 'person' | 'color' | 'image' | 'icon' | 'font' | 'mask' | 'reference' | 'background';
  file_url?: string;
  color_value?: string;
  description?: string;
  sort_order: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export const useBrandAssets = (brandId: string) => {
  return useQuery({
    queryKey: ['brand-assets', brandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_assets')
        .select('*')
        .eq('brand_id', brandId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BrandAsset[];
    },
    enabled: !!brandId,
  });
};

// Helper function to normalize asset names to variable names
const normalizeAssetName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Helper function to generate unique variable name
const generateUniqueVariableName = async (baseName: string, brandId: string, excludeId?: string): Promise<string> => {
  const normalizedBase = normalizeAssetName(baseName);
  
  if (!normalizedBase) {
    throw new Error('Asset name must contain at least one alphanumeric character');
  }

  // Check for existing variable names
  const { data: existingAssets } = await supabase
    .from('brand_assets')
    .select('variable_name')
    .eq('brand_id', brandId);

  const existingNames = (existingAssets || [])
    .filter(asset => !excludeId || asset.variable_name !== excludeId)
    .map(asset => asset.variable_name.replace(/[{}]/g, '')); // Remove braces for comparison

  let variableName = normalizedBase;
  let counter = 2;

  // Add number suffix if name already exists
  while (existingNames.includes(variableName)) {
    variableName = `${normalizedBase}-${counter}`;
    counter++;
  }

  return `{${variableName}}`;
};

export const useAddBrandAsset = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (asset: Omit<BrandAsset, 'id' | 'created_at' | 'updated_at'>) => {
      // Generate normalized variable name based on asset name
      const variableName = await generateUniqueVariableName(asset.name, asset.brand_id);

      const { data, error } = await supabase
        .from('brand_assets')
        .insert([{ ...asset, variable_name: variableName }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-assets', data.brand_id] });
      toast({
        title: "Asset added",
        description: `${data.name} has been added to your brand assets.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add brand asset.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateBrandAsset = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<BrandAsset> & { id: string }) => {
      // If name is being updated, regenerate the variable name
      if (updates.name) {
        // Get current asset to find brand_id and current variable_name
        const { data: currentAsset } = await supabase
          .from('brand_assets')
          .select('brand_id, variable_name')
          .eq('id', id)
          .single();

        if (currentAsset) {
          const newVariableName = await generateUniqueVariableName(
            updates.name, 
            currentAsset.brand_id, 
            currentAsset.variable_name
          );
          updates.variable_name = newVariableName;
        }
      }

      const { data, error } = await supabase
        .from('brand_assets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-assets', data.brand_id] });
      toast({
        title: "Asset updated",
        description: "Your brand asset has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update brand asset.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteBrandAsset = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, brandId }: { id: string; brandId: string }) => {
      const { error } = await supabase
        .from('brand_assets')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, brandId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-assets', data.brandId] });
      toast({
        title: "Asset removed",
        description: "Your brand asset has been removed successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove brand asset.",
        variant: "destructive",
      });
    },
  });
};